import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getVisibleCharacters, type DbCharacter } from '@/api/astraea';
import {
  getReplyMaterials,
  incrementReplyUsage,
  recordReplyFeedback,
  type ReplyMaterial,
} from '@/api/materialLibrary';
import { PLATFORM_OPTIONS } from '@/lib/materialScenes';

const REPLY_TOOL_URL = 'https://xtfttu.readdy.co';

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

export default function ReplyToolPage() {
  const [chars, setChars] = useState<DbCharacter[]>([]);
  const [cid, setCid] = useState<number | null>(null);
  const [replies, setReplies] = useState<ReplyMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [platform, setPlatform] = useState('dm');

  const loadReplies = useCallback(async (characterId?: number) => {
    setLoading(true);
    try {
      const list = await getReplyMaterials(characterId ?? undefined);
      setReplies(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getVisibleCharacters().then((cs) => {
      setChars(cs);
      if (cs.length > 0) {
        setCid(cs[0].id);
        loadReplies(cs[0].id);
      } else {
        setLoading(false);
      }
    });
  }, [loadReplies]);

  useEffect(() => {
    if (cid) loadReplies(cid);
  }, [cid, loadReplies]);

  const openExternalTool = () => {
    const params = new URLSearchParams({ source: 'customer_reply' });
    window.open(`${REPLY_TOOL_URL}?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const dmReplies = replies.filter((r) => r.reply_type === 'dm' || r.scene === '私信开场');
  const recommended = dmReplies[0] || replies[0];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/" className="hover:text-foreground-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i><span>回复工具</span>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-primary-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-chat-smile-2-line text-background-50 text-2xl"></i>
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">回复工具</h1>
          <p className="text-sm text-foreground-600 mt-1">业务回复客户时，直接调用角色素材库同步过来的话术。</p>
        </div>
      </div>

      {msg && <div className="mb-4 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">{msg}</div>}

      {/* 角色选择 */}
      {chars.length > 0 && (
        <label className="block text-xs text-secondary-500 mb-4">
          选择角色
          <select
            value={cid ?? ''}
            onChange={(e) => setCid(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-secondary-200 px-3 py-2 text-sm bg-white"
          >
            {chars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      )}

      {/* 可用角色素材 */}
      <section className="rounded-2xl border border-primary-200/70 bg-gradient-to-br from-primary-50/80 via-primary-50/40 to-background-50 p-6">
        <h2 className="text-base font-semibold text-foreground-950 mb-1"><i className="ri-gallery-line text-primary-500 mr-1"></i>可用角色素材</h2>
        <p className="text-xs text-secondary-500 mb-4">从素材库「生成引流钩子」后会自动同步到这里。</p>

        <div className="flex flex-wrap gap-1 mb-4 text-xs">
          {PLATFORM_OPTIONS.map((p) => (
            <button key={p.value} type="button" onClick={() => setPlatform(p.value)} className={`px-2 py-0.5 rounded cursor-pointer ${platform === p.value ? 'bg-primary-200 text-primary-800' : 'bg-white border border-secondary-200'}`}>{p.label}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-secondary-400">读取中…</p>
        ) : replies.length === 0 ? (
          <div className="text-sm text-foreground-600">
            <p>还没有同步过来的话术。</p>
            <Link to="/materials" className="inline-flex mt-3 text-primary-600 font-medium">去角色素材库生成引流钩子 →</Link>
          </div>
        ) : (
          <>
            {recommended && (
              <div className="rounded-xl border border-primary-200 bg-white p-4 mb-4 text-sm">
                <div className="text-xs font-semibold text-primary-700 mb-2">推荐回复素材</div>
                <div className="text-xs text-secondary-500">场景：{recommended.scene || '—'} · 类型：{recommended.reply_type}</div>
                <p className="mt-2 text-foreground-800 leading-relaxed">{recommended.content}</p>
                {recommended.next_question && (
                  <p className="mt-2 text-xs text-secondary-600"><span className="font-medium">下一句追问：</span>{recommended.next_question}</p>
                )}
                {recommended.lead_asset && (
                  <p className="mt-1 text-xs text-secondary-600"><span className="font-medium">承接资料：</span>{recommended.lead_asset}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button type="button" onClick={() => { copyText(recommended.content || ''); incrementReplyUsage(recommended.id); setMsg('已复制'); }} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold cursor-pointer">复制</button>
                  <button type="button" onClick={() => incrementReplyUsage(recommended.id).then(() => setMsg('已标记使用'))} className="px-3 py-1.5 rounded-lg border border-secondary-300 text-xs cursor-pointer">标记已使用</button>
                  <button type="button" onClick={() => recordReplyFeedback(recommended.id, cid!, 'replied', platform).then(() => setMsg('已标记有效'))} className="px-3 py-1.5 rounded-lg border border-green-300 text-green-700 text-xs cursor-pointer">有效</button>
                  <button type="button" onClick={() => recordReplyFeedback(recommended.id, cid!, 'rejected', platform).then(() => setMsg('已标记无效'))} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs cursor-pointer">无效</button>
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {replies.map((r) => (
                <div key={r.id} className="rounded-lg border border-secondary-200 bg-white p-3 text-xs">
                  <div className="flex justify-between gap-2 mb-1">
                    <span className="text-secondary-500">{r.scene} · {r.reply_type}</span>
                    <span className="text-secondary-400">使用 {r.usage_count} 次</span>
                  </div>
                  <p className="text-foreground-800">{r.content}</p>
                  {r.next_question && <p className="text-secondary-500 mt-1">追问：{r.next_question}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button" onClick={() => { copyText(r.content || ''); incrementReplyUsage(r.id); }} className="text-primary-600 cursor-pointer">复制</button>
                    <button type="button" onClick={() => recordReplyFeedback(r.id, cid!, 'replied', platform)} className="text-green-600 cursor-pointer">有效</button>
                    <button type="button" onClick={() => recordReplyFeedback(r.id, cid!, 'rejected', platform)} className="text-red-600 cursor-pointer">无效</button>
                  </div>
                  {(r.effect_feedback as { result?: string })?.result && (
                    <p className="text-[10px] text-secondary-400 mt-1">上次反馈：{(r.effect_feedback as { result: string }).result}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* 进阶外部工具 */}
      <section className="mt-6 rounded-2xl border border-secondary-200 bg-background-50 p-5">
        <h2 className="text-sm font-semibold text-foreground-900 mb-1">进阶：完整回复优化器</h2>
        <p className="text-xs text-foreground-600 mb-3">需要更细的语气调校与合规检查时，可开启外部完整版工具。</p>
        <button type="button" onClick={openExternalTool} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold border border-secondary-300 cursor-pointer">
          <i className="ri-external-link-line"></i>开启完整回复优化器
        </button>
      </section>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/materials" className="inline-flex items-center gap-1.5 text-sm text-primary-600 cursor-pointer">
          <i className="ri-gallery-line"></i>角色素材库
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-secondary-500 hover:text-foreground-700 cursor-pointer">
          <i className="ri-arrow-left-line"></i>返回首页
        </Link>
      </div>
    </div>
  );
}
